import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { addDays, format } from "date-fns";
import { getPropAvailabiltyCal } from "@services/krent.service";
import { DateRange } from "./PriceCard";
import { toast } from "react-toastify";
import { formatPrice, parseCalendarDate } from "@utils/helpers";
import {
  getAvailabilityFetchRange,
  parseAvailabilityCalendarResponse,
} from "@utils/availabilityCalendar";
import "./buyer-cal-picker.css";

interface BuyerCalPickerProps {
  type: "shortlet" | "long lease" | "sale";
  dateFrom: Date | null;
  setDateFrom: (date: Date | null) => void;
  dateTo: Date | null;
  setDateTo: (date: Date | null) => void;
  propertyId: string;
  surgeDays: { date: string; price: number }[];
  surgeDateRanges?: DateRange[];
  setSurgeData: (
    surgeDays: { date: string; price: number }[],
    surgeDateRanges: DateRange[],
  ) => void;
  normalPrice: number;
  partyPrice?: string | number;
  isPartyPrice?: boolean;
}

const BuyerCalPicker: React.FC<BuyerCalPickerProps> = ({
  type,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  propertyId,
  setSurgeData,
  normalPrice,
  partyPrice,
  isPartyPrice,
}) => {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [unavailableDates, setUnavailableDates] = useState<string[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [isCheckInOpen, setIsCheckInOpen] = useState(false);
  const [isCheckOutOpen, setIsCheckOutOpen] = useState(false);

  const isCalendarClick = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    Boolean(target.closest(".react-datepicker"));

  const openCheckInPicker = () => setIsCheckInOpen(true);

  const handleCheckInRowClick = (e: React.MouseEvent) => {
    if (isCalendarClick(e.target)) return;
    openCheckInPicker();
  };

  const openCheckOutPicker = () => {
    if (type === "long lease") return;
    if (!dateFrom) {
      toast.info("Please select a check-in date first");
      return;
    }
    setIsCheckOutOpen(true);
  };

  const handleCheckOutRowClick = (e: React.MouseEvent) => {
    if (isCalendarClick(e.target)) return;
    openCheckOutPicker();
  };

  const applySurgeSelection = useCallback(
    (
      surgeDaysWithPrices: { date: string; price: number }[],
      surgeDateRanges: DateRange[],
    ) => {
      if (dateFrom && dateTo) {
        const formattedDateFrom = format(dateFrom, "yyyy-MM-dd");
        const formattedDateTo = format(dateTo, "yyyy-MM-dd");

        setSurgeData(
          surgeDaysWithPrices.filter(
            (item) =>
              item.date >= formattedDateFrom && item.date <= formattedDateTo,
          ),
          surgeDateRanges.filter(
            ({ start, end }) =>
              format(start, "yyyy-MM-dd") <= formattedDateTo &&
              format(end, "yyyy-MM-dd") >= formattedDateFrom,
          ),
        );
        return;
      }

      setSurgeData(surgeDaysWithPrices, surgeDateRanges);
    },
    [dateFrom, dateTo, setSurgeData],
  );

  const fetchAvailability = useCallback(async () => {
    if (!propertyId) return;

    setLoadingAvailability(true);
    try {
      const { startDate, endDate } = getAvailabilityFetchRange();
      const response: any = await getPropAvailabiltyCal(
        propertyId,
        startDate,
        endDate,
      );

      if (!response?.status || !response.data) {
        return;
      }

      const parsed = parseAvailabilityCalendarResponse(
        response.data,
        normalPrice,
      );

      applySurgeSelection(parsed.surgeDays, parsed.surgeDateRanges);
      setPrices(parsed.priceMap);
      setUnavailableDates(parsed.unavailableDates);
    } catch (error) {
      console.error("Error fetching availability data:", error);
    } finally {
      setLoadingAvailability(false);
    }
  }, [propertyId, normalPrice]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchAvailability();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleDateFromChange = (date: Date | null) => {
    setDateFrom(date);
    if (date) setIsCheckInOpen(false);
    if (!date) return;

    if (type === "long lease") {
      const oneYearLater = addDays(date, 365);
      setDateTo(oneYearLater);
    } else if (dateTo && dateTo <= date) {
      setDateTo(null);
    }
  };

  const handleDateToChange = (date: Date | null) => {
    if (!dateFrom || !date) return;

    let isInvalidSelection = false;
    let tempDate = new Date(dateFrom);
    tempDate.setDate(tempDate.getDate() + 1);

    while (tempDate <= date) {
      const formattedDate = format(tempDate, "yyyy-MM-dd");

      if (
        unavailableDates.includes(formattedDate) &&
        formattedDate !== format(new Date(dateFrom), "yyyy-MM-dd") &&
        formattedDate !== format(date, "yyyy-MM-dd")
      ) {
        isInvalidSelection = true;
        break;
      }

      tempDate.setDate(tempDate.getDate() + 1);
    }

    if ((type === "shortlet" || type === "long lease") && isInvalidSelection) {
      toast.error("You cannot select across unavailable dates.");
      return;
    }

    setDateTo(date);
    setIsCheckOutOpen(false);
  };

  const renderDayContents = (day: number, date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateString = format(date, "yyyy-MM-dd");
    const basePrice = prices[dateString] ? prices[dateString] : normalPrice;
    const displayPrice =
      isPartyPrice && partyPrice ? Number(partyPrice) : basePrice;

    const isUnavailable = unavailableDates.includes(dateString);
    const isPast = date < today;

    return (
      <div
        className="px-1"
        style={{
          textAlign: "center",
          opacity: isUnavailable || isPast ? 0.5 : 1,
          backgroundColor: isUnavailable
            ? "#fde8e8"
            : isPast
              ? "#f0f0f0"
              : "transparent",
          borderRadius: "4px",
          cursor: isUnavailable || isPast ? "not-allowed" : "pointer",
        }}
      >
        <h6
          style={{
            fontSize: "9px",
            color: isUnavailable ? "#887171" : isPast ? "#999999" : "green",
          }}
          className={`${isUnavailable || isPast ? "" : "fw-bold"}`}
        >
          {loadingAvailability && !prices[dateString]
            ? "…"
            : formatPrice(displayPrice)}
        </h6>
        <span className={`${isUnavailable || isPast ? "" : "fw-bold"}`}>
          {day}
        </span>
      </div>
    );
  };

  const unavailableDateObjects = unavailableDates.map((date) =>
    parseCalendarDate(date),
  );

  const datePickerPopperProps = {
    popperClassName: "buyer-cal-picker__popper",
    calendarClassName: "buyer-cal-picker__calendar",
    popperPlacement: "bottom-start" as const,
    popperModifiers: [
      {
        name: "offset",
        options: { offset: [0, 8] },
      },
      {
        name: "preventOverflow",
        options: {
          rootBoundary: "viewport",
          padding: 16,
        },
      },
      {
        name: "flip",
        options: {
          fallbackPlacements: ["bottom-start", "top-start"],
        },
      },
    ],
  };

  if (type === "sale") return null;

  return (
    <div
      className="border rounded-4 buyer-cal-picker"
      style={{ borderColor: "#dee2e6" }}
    >
      <div
        className="d-flex align-items-center justify-content-between px-3 py-3 buyer-cal-picker-row"
        style={{ borderBottom: "1px solid #dee2e6", cursor: "pointer" }}
        onClick={handleCheckInRowClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openCheckInPicker();
          }
        }}
      >
        <div className="flex-grow-1 buyer-cal-picker-field">
          <div
            className="text-uppercase fw-bold mb-1 font-onest"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              color: "#212529",
            }}
          >
            {type === "shortlet" ? "Check-In" : "From"}
          </div>
          <DatePicker
            selected={dateFrom}
            onChange={handleDateFromChange}
            onSelect={(date) => {
              if (date) setIsCheckInOpen(false);
            }}
            open={isCheckInOpen}
            shouldCloseOnSelect
            onClickOutside={() => setIsCheckInOpen(false)}
            onCalendarClose={() => setIsCheckInOpen(false)}
            placeholderText={`Select ${type === "shortlet" ? "check-in" : "start"} date`}
            minDate={new Date()}
            showYearDropdown
            showMonthDropdown
            dateFormat="M/d/yyyy"
            renderDayContents={renderDayContents}
            excludeDates={
              type === "shortlet" || type === "long lease"
                ? unavailableDateObjects
                : undefined
            }
            onKeyDown={(e) => {
              if (e.key.match(/[a-zA-Z]/)) {
                e.preventDefault();
              }
            }}
            {...datePickerPopperProps}
            customInput={
              <input
                readOnly
                className="border-0 bg-transparent p-0 font-onest"
                style={{
                  fontSize: "14px",
                  color: "#495057",
                  outline: "none",
                  cursor: "pointer",
                  width: "100%",
                  pointerEvents: "none",
                }}
              />
            }
          />
        </div>
        <i className="fa-regular fa-calendar buyer-cal-picker-icon"></i>
      </div>

      <div
        className={`d-flex justify-content-between px-3 py-3 buyer-cal-picker-row ${
          type === "shortlet" && !dateFrom
            ? "align-items-start"
            : "align-items-center"
        }`}
        style={{
          cursor: type === "long lease" ? "not-allowed" : "pointer",
        }}
        onClick={handleCheckOutRowClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openCheckOutPicker();
          }
        }}
      >
        <div className="flex-grow-1 buyer-cal-picker-field">
          <div
            className="text-uppercase fw-bold mb-1 font-onest"
            style={{
              fontSize: "11px",
              letterSpacing: "0.05em",
              color: "#212529",
            }}
          >
            {type === "shortlet" ? "Checkout" : "To"}
          </div>
          <DatePicker
            selected={dateTo}
            onChange={handleDateToChange}
            onSelect={(date) => {
              if (date) setIsCheckOutOpen(false);
            }}
            open={isCheckOutOpen}
            shouldCloseOnSelect
            onClickOutside={() => setIsCheckOutOpen(false)}
            onCalendarClose={() => setIsCheckOutOpen(false)}
            placeholderText={`Select ${type === "shortlet" ? "checkout" : "end"} date`}
            minDate={dateFrom ? addDays(new Date(dateFrom), 1) : null}
            showYearDropdown
            showMonthDropdown
            disabled={!dateFrom || type === "long lease"}
            dateFormat="M/d/yyyy"
            renderDayContents={renderDayContents}
            excludeDates={
              type === "shortlet" || type === "long lease"
                ? unavailableDateObjects.filter((date) => {
                    if (dateFrom) {
                      const formattedDateFrom = format(dateFrom, "yyyy-MM-dd");
                      const firstUnavailable = unavailableDates.find(
                        (d) => d > formattedDateFrom,
                      );
                      if (
                        firstUnavailable &&
                        format(date, "yyyy-MM-dd") === firstUnavailable
                      ) {
                        return false;
                      }
                    }
                    return date > (dateFrom ? new Date(dateFrom) : new Date());
                  })
                : undefined
            }
            onKeyDown={(e) => {
              if (e.key.match(/[a-zA-Z]/)) {
                e.preventDefault();
              }
            }}
            {...datePickerPopperProps}
            customInput={
              <input
                readOnly
                className="border-0 bg-transparent p-0 font-onest"
                style={{
                  fontSize: "14px",
                  color:
                    !dateFrom || type === "long lease" ? "#adb5bd" : "#495057",
                  outline: "none",
                  cursor: "inherit",
                  width: "100%",
                  pointerEvents: "none",
                }}
              />
            }
          />
          {type === "shortlet" && !dateFrom && (
            <div className="d-flex align-items-center gap-1 mt-1 font-onest buyer-cal-picker-hint">
              <i className="fa-solid fa-circle-info" aria-hidden="true"></i>
              <span>Select check-in date first</span>
            </div>
          )}
        </div>
        <i
          className={`fa-regular fa-calendar buyer-cal-picker-icon ${
            !dateFrom && type === "shortlet"
              ? "buyer-cal-picker-icon--disabled buyer-cal-picker-icon--aligned-top"
              : ""
          }`}
        ></i>
      </div>
    </div>
  );
};

export default BuyerCalPicker;
